/** ************************************************************************
 * Page for the Display Offer single-page inside of the iFrame.
 *
 * Routing to the displayoffer shows this page.
 * This page is the displayoffer iFrame, which has the entire offer details.
 * This page is meant to be displayed in a partner's website, inside of
 * an HTML element they designate for the iFrame.
 *
 * @file   Single-page file
 * @author Paul J Stales
 * @since  2.0.0
 ************************************************************************ */

import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { css } from 'aphrodite'
import styled from 'styled-components'
import * as templates from '../../templates'
import { getBperxOfferData } from '../../services/bperxOfferDataService'
import { ProductMagazine } from '../../../../../common/types/productTypes/ProductMagazine'
import {
  sendFrameHeightViaPostMessage,
  sendReportableEventViaPostMessage
} from '../../services/iframePostMessageService'
import { TransactionInformation } from '../../../../../common/types/transactionInformationTypes/TransactionInformation'
import { PartnerSuppliedInfo } from '../../../../../common/types/partnerSuppliedInfoTypes/PartnerSuppliedInfo'
import { DisplayEvent } from '../../../../../common/types/reportableEvents/subTypes/DisplayEvent'
import { ReportableEventType } from '../../../../../common/types/reportableEvents/ReportableEvent'

// Constants and Variables
const IFRAME_DISPLAYOFFER_ID = '__________BPERX_DISPLAYOFFER__________'
let heightTimeout: NodeJS.Timeout

/**
 * Interface type for DisplayOfferProps
 */
interface DisplayOfferProps {
  path: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: { [key: string]: any }
  isTransactionInformationAvailable: boolean
  transactionInformation: TransactionInformation
  setTransactionInformation: (ti: TransactionInformation) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
  isPartnerInfoAvailable: boolean
  partnerSuppliedInfo: PartnerSuppliedInfo
}

/**
 * Functional component for the entire offer display.
 *
 * This displayOffer functional component appears when routing goes to /displayOffer.
 * This shows the currently selected offer, terms link, opt status checkbox,
 * button to control product visibility, and products in a grid.
 *
 * Customer-Driven UI State
 * @STATE: height: the height the iFrame needs to display all of its offers
 * @STATE: areProductsVisible: whether or not the offers are visible (triggered by the
 *         "Change My Selection" button)
 *
 * Data-Driven State
 * @STATE: berxProducts: the products being offered
 *
 * @param DisplayOfferProps
 * @returns
 */
const DisplayOffer = ({
  path,
  styles,
  isTransactionInformationAvailable,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation,
  isPartnerInfoAvailable,
  partnerSuppliedInfo
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
DisplayOfferProps): any => {
  // ========== State =============================================================================

  // Customer-Driven UI State
  const [height, setHeight] = useState(0)
  const [areProductsVisible, setProductsVisible] = useState(false)
  const [areTermsVisible, setTermsVisible] = useState(false)

  // Opt Logic
  const [isIntentToOptIn, setActionElementPositiveIntentToOptIn] =
    useState(true)

  // Data-Driven State
  const [bperxProducts, setBperxProducts] = useState<ProductMagazine[]>()

  // Reportable Event State
  const [displayEvent, setDisplayEvent] = useState<DisplayEvent>({
    // general reportable event properties
    reportableEventType: ReportableEventType.NOT_YET_DETERMINED,
    timestamp: '',
    partnerId: partnerSuppliedInfo.frameInformation.partnerId,
    offerId: partnerSuppliedInfo.frameInformation.offerId,
    placementName: partnerSuppliedInfo.frameInformation.placementName,
    subID1: partnerSuppliedInfo.partnerCustomizations?.subIDs?.subID1,
    subID2: partnerSuppliedInfo.partnerCustomizations?.subIDs?.subID2,
    subID3: partnerSuppliedInfo.partnerCustomizations?.subIDs?.subID3,
    subID4: partnerSuppliedInfo.partnerCustomizations?.subIDs?.subID4,
    subID5: partnerSuppliedInfo.partnerCustomizations?.subIDs?.subID5,
    productCode:
      transactionInformation.customerChoice.productMagazineChosen?.productCode,
    imageURL: `https:${transactionInformation.customerChoice.productMagazineChosen?.coverImageURL}`,
    title: transactionInformation.customerChoice.productMagazineChosen?.title,
    isOptingIn: transactionInformation.customerChoice.isOptingIn,
    didViewProducts: transactionInformation.customerChoice.didViewProducts,
    bPerxVoucherCode: transactionInformation.voucherInformation?.voucherCode,
    // reportable display event specific properties
    isIntentToOptIn: partnerSuppliedInfo.partnerCustomizations?.isIntentToOptIn,
    defaultOptingIn: partnerSuppliedInfo.partnerCustomizations?.defaultOptingIn,
    isOptElementAlwaysVisible:
      partnerSuppliedInfo.partnerCustomizations?.isOptElementAlwaysVisible,
    autoRotateProducts:
      partnerSuppliedInfo.partnerCustomizations?.autoRotateProducts,
    suppressProducts:
      partnerSuppliedInfo.partnerCustomizations?.suppressProducts
  })

  // ========== Use Effect Hooks ==================================================================

  // ----- [UE1] When TransactionInformation and PartnerInfo are available, get the bperx offer data
  useEffect(() => {
    if (isTransactionInformationAvailable && isPartnerInfoAvailable) {
      // set the opt to the default
      const updateTransactionInformation: TransactionInformation = {
        ...transactionInformation
      }

      updateTransactionInformation.customerChoice.isOptingIn =
        !!partnerSuppliedInfo?.partnerCustomizations?.defaultOptingIn

      setTransactionInformation(updateTransactionInformation)
      setDoesPartnerNeedUpdateForTransactionInformation(true)

      // get bperx offer data
      getBperxOfferData(
        setBperxProducts,
        partnerSuppliedInfo,
        transactionInformation,
        setTransactionInformation,
        setActionElementPositiveIntentToOptIn,
        setDoesPartnerNeedUpdateForTransactionInformation,
        displayEventHandler,
        displayEvent,
        setDisplayEvent,
        transactionInformation
      )
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransactionInformationAvailable, isPartnerInfoAvailable])

  // ----- [UE2] The products being shown, hidden, etc. affects height size
  useEffect(() => {
    if (heightTimeout) clearTimeout(heightTimeout)
    heightTimeout = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setHeight(document.getElementById(IFRAME_DISPLAYOFFER_ID)!.offsetHeight)
    }, 250)

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areProductsVisible,
    bperxProducts,
    transactionInformation.customerChoice.isOptingIn
  ])

  // ----- [UE3] When the height changes, tell the partner/parent window
  useEffect(() => {
    sendFrameHeightViaPostMessage(
      height,
      partnerSuppliedInfo.frameInformation.partnerId
    )
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height])

  // ----- [UE4] When the terms are shown/hidden, tell the partner/parent window
  useEffect(() => {
    if (heightTimeout) clearTimeout(heightTimeout)
    heightTimeout = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setHeight(document.getElementById(IFRAME_DISPLAYOFFER_ID)!.offsetHeight)
    }, 1)

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areTermsVisible])

  // ----- [UE5] When a reportable display event occurs, inform the partner/parent window
  useEffect(() => {
    if (displayEvent.productCode !== undefined) {
      sendReportableEventViaPostMessage(
        displayEvent,
        partnerSuppliedInfo.frameInformation.partnerId
      )
    }

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayEvent])

  // ========== Event Listeners for Window ========================================================

  addEventListener('load', () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setHeight(document.getElementById(IFRAME_DISPLAYOFFER_ID)!.offsetHeight)
  })

  addEventListener('resize', () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setHeight(document.getElementById(IFRAME_DISPLAYOFFER_ID)!.offsetHeight)
  })

  // ========== Return the TSX ====================================================================

  // Display nothing if no products are loaded
  if (!bperxProducts?.length) {
    return <div id={IFRAME_DISPLAYOFFER_ID} />
  }

  // Get the template to display
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Template = (templates as any)[
    partnerSuppliedInfo.frameInformation.templateChoice || 'Default'
  ].default

  const ScrollbarWrapper = styled.div(() => ({
    '::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      height: `${
        partnerSuppliedInfo.partnerCustomizations?.customCss?.scrollbarSize || 8
      }px`,
      weight: `${
        partnerSuppliedInfo.partnerCustomizations?.customCss?.scrollbarSize || 8
      }px`
    },
    '::-webkit-scrollbar-thumb': {
      borderRadius: '4px',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      WebkitBoxShadow: '0 0 1px rgba(255, 255, 255, 0.5)'
    }
  }))

  // Return the TSX
  return (
    <ScrollbarWrapper>
      <div id={IFRAME_DISPLAYOFFER_ID}>
        <Template
          styles={styles}
          isIntentToOptIn={isIntentToOptIn}
          transactionInformation={transactionInformation}
          setTransactionInformation={setTransactionInformation}
          bperxProducts={bperxProducts}
          areProductsVisible={areProductsVisible}
          setProductsVisible={setProductsVisible}
          areTermsVisible={areTermsVisible}
          setTermsVisible={setTermsVisible}
          setDoesPartnerNeedUpdateForTransactionInformation={
            setDoesPartnerNeedUpdateForTransactionInformation
          }
          partnerSuppliedInfo={partnerSuppliedInfo}
          displayEvent={displayEvent}
          setDisplayEvent={setDisplayEvent}
          displayEventHandler={displayEventHandler}
        />
      </div>
    </ScrollbarWrapper>
  )
}

/**
 * Handle the ReportableEvent, DisplayEvent, allowing for the
 * parent/partner to receive an event for reporting.
 *
 * There is a useEffect triggered by this that will update the
 * parent/partner window about the updated Reportable Display Event.
 *
 * @param displayEvent
 * @param setDisplayEvent
 * @param newTransactionInformation
 */
function displayEventHandler(
  displayEvent: DisplayEvent,
  setDisplayEvent: (displayEvent: DisplayEvent) => void,
  newTransactionInformation: TransactionInformation,
  reportableEventType: string
): void {
  const NEW_DISPLAY_EVENT: DisplayEvent = {
    ...displayEvent
  }
  NEW_DISPLAY_EVENT.reportableEventType = reportableEventType
  NEW_DISPLAY_EVENT.timestamp = new Date().toLocaleString()
  NEW_DISPLAY_EVENT.productCode =
    newTransactionInformation.customerChoice.productMagazineChosen.productCode
  NEW_DISPLAY_EVENT.isOptingIn =
    newTransactionInformation.customerChoice.isOptingIn
  NEW_DISPLAY_EVENT.imageURL = `https:${newTransactionInformation.customerChoice.productMagazineChosen?.coverImageURL}`
  NEW_DISPLAY_EVENT.title =
    newTransactionInformation.customerChoice.productMagazineChosen.title
  NEW_DISPLAY_EVENT.bPerxVoucherCode =
    newTransactionInformation.voucherInformation.voucherCode
  NEW_DISPLAY_EVENT.didViewProducts = true

  setDisplayEvent(NEW_DISPLAY_EVENT)
}

// ========== Export ====================================================================

export default DisplayOffer
